/**
 * CDB54 Calendrier+ - Accessibility Composable
 * Story 2.7c: Font size adjustment
 * Story 2.7d: Reduced motion support
 */

import { ref, watch, onMounted } from 'vue';

export type FontSize = 'normal' | 'large' | 'xlarge';

const FONT_SIZE_KEY = 'cdb54_font_size';
const FONT_SIZE_MAP: Record<FontSize, string> = {
  normal: '16px',
  large: '18px',
  xlarge: '20px',
};

// Singleton state to share across components
const fontSize = ref<FontSize>('normal');
const prefersReducedMotion = ref(false);

/**
 * Accessibility composable for font size and motion preferences
 */
export function useAccessibility() {
  /**
   * Initialize accessibility settings
   */
  function initialize(): void {
    // Load saved font size preference
    const savedSize = localStorage.getItem(FONT_SIZE_KEY) as FontSize | null;
    if (savedSize && savedSize in FONT_SIZE_MAP) {
      fontSize.value = savedSize;
    }

    // Apply font size to document
    applyFontSize(fontSize.value);

    // Check for reduced motion preference
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      prefersReducedMotion.value = mediaQuery.matches;

      // Listen for changes
      mediaQuery.addEventListener('change', (e) => {
        prefersReducedMotion.value = e.matches;
      });
    }
  }

  /**
   * Apply font size to document root
   */
  function applyFontSize(size: FontSize): void {
    document.documentElement.style.setProperty('--cdb54-base-font-size', FONT_SIZE_MAP[size]);
    document.documentElement.style.fontSize = FONT_SIZE_MAP[size];
  }

  /**
   * Set font size and persist
   */
  function setFontSize(size: FontSize): void {
    fontSize.value = size;
    localStorage.setItem(FONT_SIZE_KEY, size);
    applyFontSize(size);
  }

  /**
   * Get available font sizes for settings UI
   */
  function getFontSizeOptions(): { value: FontSize; label: string }[] {
    return [
      { value: 'normal', label: 'Normale (16px)' },
      { value: 'large', label: 'Grande (18px)' },
      { value: 'xlarge', label: 'Très grande (20px)' },
    ];
  }

  // Initialize on first use
  onMounted(() => {
    initialize();
  });

  // Watch for font size changes
  watch(fontSize, (newSize) => {
    applyFontSize(newSize);
  });

  return {
    fontSize,
    prefersReducedMotion,
    setFontSize,
    getFontSizeOptions,
    initialize,
  };
}
